import React, { useMemo } from 'react';
import { CheckCircle2, ClipboardCheck, Clock, Hourglass } from 'lucide-react';
import { ReviewerQueueTable } from './ReviewerQueueTable.tsx';
import { ReviewerSummaryCard } from './ReviewerSummaryCard.tsx';
import { ReviewerPendingByTesterChart } from './ReviewerPendingByTesterChart.tsx';
import { PageHeader, SectionHeader } from '../controller/shared.tsx';
import { useReviewerQueue, useReviewerSummary } from '../../../hooks/analytics/useReviewerReports.ts';
import { AnalyticsSlicerBar, type SlicerDef } from '../AnalyticsSlicerBar.tsx';
import { AnalyticsTabs } from '../AnalyticsTabs.tsx';
import { KpiRow, type KpiItem } from '../KpiRow.tsx';
import { applySlicers, deriveSlicerOptions, useSlicerState } from '../slicerUtils.ts';

const SLICER_KEYS = ['tester_name', 'workflow_status'];

/**
 * Reviewer persona analytics dashboard — the 6th persona added in the
 * 2026-08 feedback round (`dashboard_key: 'reviewer'`). Read-only, same IA
 * as the other 5 persona pages: PageHeader -> slicer bar -> always-visible
 * KPI row -> tabbed body.
 *
 * The slicer bar and KPI row are driven off the Review Queue rows (pending-
 * review runs) with Tester/Status slicers; `reviewer_summary`'s `by_tester`
 * breakdown is a separate pre-aggregated dataset (org-wide, not itself
 * filterable by the same rows) so the Pending by Tester chart stays
 * independent of the slicer bar, same judgment call the other dashboards
 * make for their own org-level aggregates.
 */
export const ReviewerDashboard: React.FC = () => {
  const { data: queueData } = useReviewerQueue();
  const queueRows = useMemo(() => queueData?.items ?? [], [queueData]);
  const { data: summaryData } = useReviewerSummary();

  const slicers = useSlicerState(SLICER_KEYS);

  const slicerDefs = useMemo<SlicerDef[]>(
    () => [
      { key: 'tester_name', label: 'Tester', options: deriveSlicerOptions(queueRows, (r) => r.tester_name) },
      { key: 'workflow_status', label: 'Status', options: deriveSlicerOptions(queueRows, (r) => r.workflow_status) },
    ],
    [queueRows]
  );

  const filteredQueueRows = useMemo(
    () =>
      applySlicers(queueRows, slicers.values, {
        tester_name: (r) => r.tester_name,
        workflow_status: (r) => r.workflow_status,
      }),
    [queueRows, slicers.values]
  );

  const kpis = useMemo<KpiItem[]>(() => {
    const waitingOver5 = filteredQueueRows.filter((r) => r.days_waiting >= 5).length;
    return [
      {
        key: 'pending',
        title: 'Pending Review',
        value: filteredQueueRows.length,
        icon: <Hourglass className="h-5 w-5" />,
        color: 'bg-amber-100 text-amber-600',
        subtitle: `of ${summaryData?.pending_review ?? 0} org-wide`,
      },
      {
        key: 'reviewed',
        title: 'Reviewed',
        value: summaryData?.reviewed_count ?? 0,
        icon: <CheckCircle2 className="h-5 w-5" />,
        color: 'bg-emerald-100 text-emerald-600',
      },
      {
        key: 'avg_waiting',
        title: 'Avg. Days Waiting',
        value: summaryData?.avg_days_waiting != null ? summaryData.avg_days_waiting.toFixed(1) : '—',
        icon: <Clock className="h-5 w-5" />,
        color: 'bg-blue-100 text-blue-600',
      },
      {
        key: 'waiting_5plus',
        title: 'Waiting 5+ Days',
        value: waitingOver5,
        icon: <ClipboardCheck className="h-5 w-5" />,
        color: 'bg-rose-100 text-rose-600',
        subtitle: 'in current filter',
      },
    ];
  }, [filteredQueueRows, summaryData]);

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          icon={<ClipboardCheck className="h-5 w-5" />}
          iconClassName="bg-teal-50 text-teal-600"
          title="Reviewer Dashboard"
          description="Runs awaiting your review, oldest first"
        />

        <AnalyticsSlicerBar
          slicers={slicerDefs}
          values={slicers.values}
          onChange={slicers.setValue}
          onClear={slicers.clear}
          activeCount={slicers.activeCount}
        />

        <KpiRow items={kpis} />

        <AnalyticsTabs
          tabs={[
            {
              key: 'overview',
              label: 'Overview',
              content: (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ReviewerSummaryCard />
                  <ReviewerPendingByTesterChart />
                </div>
              ),
            },
            {
              key: 'raw',
              label: 'Raw Data',
              content: (
                <section>
                  <SectionHeader title="Review Queue" description="Pending-review runs, oldest first" />
                  <ReviewerQueueTable rows={filteredQueueRows} />
                </section>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default ReviewerDashboard;
